import FormContent from 'components/layout/FormContent';
import styled from 'styled-components';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { PatientDuplicateResponse, PatientDuplicateResponseInfo } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import moment from 'moment';
import { isKeyCheck } from 'utils/keyUtils';
import { Button } from 'semantic-ui-react';
import FormButtonsRow from 'components/layout/FormButtonsRow';

const StyleTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__rowColumn:nth-child(10) {
    border-right: none;
    white-space: pre-wrap;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
    white-space: pre-wrap;
  }
`;

const TableWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 809px;
  max-height: 809px;
  padding-bottom: 1.5rem;

  .patients-search-container {
    margin-top: 2rem;
    flex: 1 1 auto;
  }
`;

const rowStyle = {
  cursor: "pointer",
  userSelect: "none"
};

const selectedRow = {
  ...rowStyle,
  backgroundColor: "#e0e0e0"
};

const UpsertPatientMergingFormContent = styled(FormContent)`
  max-width: 570px;
`

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  setDuplicatePatientIx: (index?: number) => void,
  onCancel: () => void,
  onSave: (selectedPatient: PatientDuplicateResponse) => void,
  duplicatePatients?: PatientDuplicateResponseInfo,
}

interface State {
  errorMessage?: string,
  selectedPatient?: PatientDuplicateResponse,
  selectedPatientIx?: number,
  cancelTokenSource: CancelTokenSource
}

class DuplicatePatientsListModal extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = () => {

    const {onSave} = this.props;
    const {selectedPatient} = this.state;

    if (selectedPatient) {
      return onSave(selectedPatient);
    }

  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };


  markPatient = (patientIndex: number) => {

    const {duplicatePatients} = this.props;
    if (duplicatePatients && duplicatePatients.patientDuplicateData && duplicatePatients.patientDuplicateData[patientIndex]) {
      this.setState({
        selectedPatient: duplicatePatients.patientDuplicateData[patientIndex]
      });
      this.props.setDuplicatePatientIx(patientIndex)
    }
  };

  render(): React.ReactNode {

    return this.renderDuplicatedPatients();
  }

  onSelectPatientRow(foundPatient: any, index: any) {
    this.setState({
      selectedPatient: foundPatient,
      selectedPatientIx: index
    });
  };

  changeSelectedPatient(event: any, source?: string) {
    event.persist();
    const {selectedPatientIx} = this.state;
    const {duplicatePatients} = this.props;

    const isKey: any = isKeyCheck(event);

    if (selectedPatientIx !== undefined && duplicatePatients) {

      const patientMergingDatas = duplicatePatients.patientDuplicateData;

      const goDown = patientMergingDatas.length > 0 && (selectedPatientIx + 1 <= patientMergingDatas.length - 1);
      const goUp = patientMergingDatas.length > 0 && (selectedPatientIx - 1 >= 0);

      if (goDown && isKey.arrowDown) {
        const index = selectedPatientIx + 1;
        this.onSelectPatientRow(patientMergingDatas[index], index);
      } else if (goUp && isKey.arrowUp) {
        const index = selectedPatientIx - 1;
        this.onSelectPatientRow(patientMergingDatas[index], index);
      }

      const tableContainer = document.getElementById('patient-search-table-container');
      const focusedTable = tableContainer ? tableContainer
          .querySelector('.ReactVirtualized__Grid.ReactVirtualized__Table__Grid') : null;

      if (tableContainer && (isKey.pageUp || isKey.pageDown)) {
        tableContainer.focus();
      }

      const activeElement = document.activeElement === tableContainer;

      if (isKey.arrowUp || isKey.pageUp || isKey.pageDown) {
        event.preventDefault();
      }

      if (isKey.pageUp && focusedTable && activeElement) {
        //@ts-ignore
        const height = focusedTable.offsetHeight;

        if (selectedPatientIx !== undefined) {
          const jumpTo = selectedPatientIx - Math.floor(height / 25);

          if (jumpTo < 0) {
            this.onSelectPatientRow(patientMergingDatas[0], 0);
          } else {
            this.onSelectPatientRow(patientMergingDatas[jumpTo], jumpTo);
          }
        }
      }

      if (isKey.pageDown && focusedTable && activeElement) {
        //@ts-ignore
        const height = focusedTable.offsetHeight;

        if (selectedPatientIx !== undefined) {

          const jumpTo = Math.floor(height / 25) + selectedPatientIx;

          if (jumpTo > patientMergingDatas.length - 1) {
            this.onSelectPatientRow(patientMergingDatas[patientMergingDatas.length - 1], patientMergingDatas.length - 1);
          } else {
            this.onSelectPatientRow(patientMergingDatas[jumpTo], jumpTo);
          }
        }
      }

      if (source) {
        const searchElementId = source === 'search' ? "patient-search-input" : "patient-first-name-search-input";
        const searchInputElement = document.getElementById(searchElementId)
        if (searchInputElement) {
          searchInputElement.focus();
        }
      }

    }

  }

  foundPatientRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {selectedPatient} = this.state;
    const {duplicatePatients} = this.props;

    let styles = {
      ...style,
      ...rowStyle
    };

    if (selectedPatient && duplicatePatients && (selectedPatient === duplicatePatients.patientDuplicateData[index])) {
      styles = {
        ...style,
        ...selectedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markPatient(index)}
            onDoubleClick={this.handleSubmit}
        >
          {columns}
        </div>
    );
  };

  birthDateCellRenderer = ({cellData}: any) => {
    if (moment(cellData).isValid()) {
      return (
          <div>{moment(cellData).format("DD.MM.YYYY")}</div>
      );
    }
    return "";
  };

  renderDuplicatedPatients = (): React.ReactNode => {

    const {getMessage, onCancel, duplicatePatients} = this.props;
    const {errorMessage, selectedPatient} = this.state;

    return (
        <UpsertPatientMergingFormContent>
          {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }
          <TableWrapperDiv>
            <div id="patient-search-table-container"
                 className="patients-search-container"
                 tabIndex={0}
                 onKeyDown={(event) => this.changeSelectedPatient(event)}
            >
              <StyleTable
                  rowCount={duplicatePatients ? duplicatePatients.patientDuplicateData.length : 0}
                  rowGetter={({index}: any) => duplicatePatients ? duplicatePatients.patientDuplicateData[index] : undefined}
                  rowRenderer={this.foundPatientRowRenderer}
                  rowHeight={42}
                  columns={[
                    {
                      width: 100,
                      label: getMessage('patient.search.table.duplicates'),
                      dataKey: "count"
                    },
                    {
                      width: 125,
                      label: getMessage('patient.search.table.name'),
                      dataKey: "name"
                    },
                    {
                      width: 125,
                      label: getMessage('patient.search.table.firstname'),
                      dataKey: "firstName"
                    },
                    {
                      width: 125,
                      label: getMessage('patient.search.table.birthdate'),
                      dataKey: "birthDate",
                      cellRenderer: this.birthDateCellRenderer
                    }
                  ]}
              />
            </div>
          </TableWrapperDiv>

          <FormButtonsRow>
            <Button compact type="button" primary disabled={!selectedPatient}
                    onClick={this.handleSubmit}
            >{getMessage("button.ok")}</Button>
            <Button compact type="button"
                    onClick={onCancel}>{getMessage("button.cancel")}</Button>
          </FormButtonsRow>
        </UpsertPatientMergingFormContent>


    );
  };

}

export default withMessages(DuplicatePatientsListModal);
